/**
 * ============================================================================
 * SCORING.TS — Motor de Lead Scoring do CRM
 * ============================================================================
 *
 * Proposito: Calcular pontuacao (score) de 0-100 para cada deal/lead,
 *   permitindo priorizacao inteligente do pipeline comercial.
 *
 * Exportacoes principais:
 *   - scoreAllDeals()            — Pontua todos os deals de uma vez (O(n))
 *   - scoreOneDeal()             — Pontua um deal individual com contexto
 *   - calculateScore()           — Scoring simples para uso externo
 *   - calculateScoreSimilarity() — Scoring baseado em similaridade com deals fechados
 *
 * Estrategia de scoring:
 *   1. Score BASICO: faixas fixas por valor, porte, origem, contatos, proposta
 *   2. Score ESTATISTICO: compara o deal com perfil dos deals ja fechados
 *      (valor medio, distribuicao de porte/origem/servico/categoria)
 *   3. Quando ha deals fechados, usa o metodo estatistico; senao, usa o basico
 *
 * Padrao de uso:
 *   const dealsComScore = scoreAllDeals(todosOsDeals);
 * ============================================================================
 */

// ── Funcoes de Lead Scoring ──
import type { Deal } from '@/store/types';

// ── Scoring Basico (Regras Fixas) ──
// Usado quando nao ha dados historicos de deals fechados para comparacao.
// Pontuacao distribuida: valor(8-40) + porte(7-25) + origem(4-15) + contatos(0-10) + proposta(5) - estagnacao
export function calculateScoreBasic(deal: Deal): number {
  let score = 0;

  // Componente 1: Faixa de valor do deal (peso maximo: 40 pontos)
  if (deal.value >= 60000) score += 40;
  else if (deal.value >= 40000) score += 32;
  else if (deal.value >= 25000) score += 24;
  else if (deal.value >= 15000) score += 16;
  else score += 8;

  // Componente 2: Porte da empresa (peso maximo: 25 pontos)
  const porteScores: Record<string, number> = { enterprise: 25, grande: 20, medio: 14, pequeno: 7 };
  score += porteScores[deal.porte || 'pequeno'] || 7;

  // Componente 3: Canal de origem do lead (peso maximo: 15 pontos)
  // Indicacao tem maior peso pois historicamente converte mais
  const originScores: Record<string, number> = { 'Indicação': 15, 'LinkedIn': 13, 'Orgânico': 11, 'Google Ads': 9, 'Meta Ads': 7, 'Email Marketing': 5, 'TikTok Ads': 4 };
  score += originScores[deal.origin as string] || 5;

  // Componente 4: Quantidade de interacoes (3 pts cada, maximo 10 pontos)
  score += Math.min((deal.contactLogs?.length || 0) * 3, 10);

  // Componente 5: Bonus por proposta anexada (+5 pontos)
  if (deal.proposalAttached) score += 5;

  // Penalidade: Estagnacao na coluna (desconta ate 10 pontos apos 5 dias)
  if (deal.daysInColumn > 5) score -= Math.min(deal.daysInColumn - 5, 10);

  // Garante que o score final fique entre 0 e 100
  return Math.max(0, Math.min(100, score));
}

// ── Scoring Estatistico (Baseado em Similaridade) ──
// Compara o deal atual com o perfil historico dos deals ja fechados.
// Quanto mais parecido com deals que fecharam, maior o score.
// Distribuicao: proximidade_valor(30) + porte(20) + origem(15) + servico(10) + categoria(10)
//   + completude(10) + interacoes(5) - estagnacao(8)
function calculateScoreWithStats(
  deal: Deal,
  avgClosedValue: number,
  porteCounts: Record<string, number>,
  originCounts: Record<string, number>,
  serviceCounts: Record<string, number>,
  catCounts: Record<string, number>,
  closedLen: number,
): number {
  let score = 0;

  // Componente 1: Proximidade do valor com a media dos fechados (peso: 30 pts)
  // Quanto menor a diferenca relativa, maior a pontuacao
  const valueDiff = avgClosedValue > 0 ? Math.abs(deal.value - avgClosedValue) / avgClosedValue : 0;
  score += Math.max(0, 30 * (1 - Math.min(valueDiff, 1)));

  // Componente 2: Proporcao do porte nos fechados (peso: 20 pts)
  if (closedLen > 0) score += ((porteCounts[deal.porte || 'pequeno'] || 0) / closedLen) * 20;

  // Componente 3: Proporcao da origem nos fechados (peso: 15 pts)
  if (closedLen > 0) score += ((originCounts[deal.origin] || 0) / closedLen) * 15;

  // Componente 4: Proporcao do servico nos fechados (peso: 10 pts)
  if (closedLen > 0) score += ((serviceCounts[deal.service] || 0) / closedLen) * 10;

  // Componente 5: Proporcao da categoria nos fechados (peso: 10 pts)
  if (closedLen > 0) score += ((catCounts[deal.clientCategory || 'b2b'] || 0) / closedLen) * 10;

  // Componente 6: Completude dos dados do deal (peso: 10 pts)
  // Conta quantos dos 10 campos chave estao preenchidos
  let completeness = 0;
  if (deal.company) completeness++;
  if (deal.contact) completeness++;
  if (deal.email) completeness++;
  if (deal.phone) completeness++;
  if (deal.porte) completeness++;
  if (deal.clientCategory) completeness++;
  if (deal.sdr) completeness++;
  if (deal.closer) completeness++;
  if (deal.value > 0) completeness++;
  if (deal.proposalAttached) completeness++;
  score += (completeness / 10) * 10;

  // Componente 7: Interacoes registradas (1.5 pts cada, maximo 5 pts)
  score += Math.min((deal.contactLogs?.length || 0) * 1.5, 5);

  // Penalidade: Estagnacao na coluna (desconta ate 8 pontos apos 7 dias)
  if (deal.daysInColumn > 7) score -= Math.min(deal.daysInColumn - 7, 8);

  // Garante score entre 0-100 e arredonda
  return Math.max(0, Math.min(100, Math.round(score)));
}

// ── Construcao de Estatisticas dos Deals Fechados ──
// Agrega contagens por porte, origem, servico e categoria dos deals ja fechados.
// Essas estatisticas alimentam o scoring por similaridade.
function buildClosedStats(closedDeals: Deal[]) {
  const closedLen = closedDeals.length;
  const avgClosedValue = closedLen > 0 ? closedDeals.reduce((sum, d) => sum + d.value, 0) / closedLen : 1;
  const porteCounts: Record<string, number> = {};
  const originCounts: Record<string, number> = {};
  const serviceCounts: Record<string, number> = {};
  const catCounts: Record<string, number> = {};
  closedDeals.forEach(d => {
    porteCounts[d.porte || 'pequeno'] = (porteCounts[d.porte || 'pequeno'] || 0) + 1;
    originCounts[d.origin] = (originCounts[d.origin] || 0) + 1;
    serviceCounts[d.service] = (serviceCounts[d.service] || 0) + 1;
    catCounts[d.clientCategory || 'b2b'] = (catCounts[d.clientCategory || 'b2b'] || 0) + 1;
  });
  return { avgClosedValue, porteCounts, originCounts, serviceCounts, catCounts, closedLen };
}

// ── Funcoes Publicas de Scoring ──

/** Pre-calcula estatisticas uma vez e pontua todos os deals em O(n) */
export function scoreAllDeals(deals: Deal[]): Deal[] {
  const closedDeals = deals.filter(d => d.column === 'fechamento');
  if (closedDeals.length === 0) return deals.map(d => ({ ...d, score: calculateScoreBasic(d) }));
  const stats = buildClosedStats(closedDeals);
  return deals.map(d => ({ ...d, score: calculateScoreWithStats(d, stats.avgClosedValue, stats.porteCounts, stats.originCounts, stats.serviceCounts, stats.catCounts, stats.closedLen) }));
}

/** Pontua um unico deal usando estatisticas da lista completa */
// ⚠️ BUG AUDIT: Esta funcao recalcula buildClosedStats() a cada chamada.
// Se chamada em loop para N deals, a complexidade sera O(N*M) onde M = deals fechados.
// Considerar usar scoreAllDeals() ou cachear as estatisticas.
export function scoreOneDeal(deal: Deal, allDeals: Deal[]): Deal {
  const closedDeals = allDeals.filter(d => d.column === 'fechamento');
  if (closedDeals.length === 0) return { ...deal, score: calculateScoreBasic(deal) };
  const stats = buildClosedStats(closedDeals);
  return { ...deal, score: calculateScoreWithStats(deal, stats.avgClosedValue, stats.porteCounts, stats.originCounts, stats.serviceCounts, stats.catCounts, stats.closedLen) };
}

/** Score simplificado para uso externo (aceita tipo parcial de Deal) */
export function calculateScore(deal: Pick<Deal, 'value' | 'porte' | 'origin' | 'daysInColumn' | 'contactLogs' | 'proposalAttached'>): number {
  return calculateScoreBasic(deal as Deal);
}

/** Calcula score por similaridade comparando com deals fechados fornecidos */
export function calculateScoreSimilarity(deal: Deal, closedDeals: Deal[]): number {
  if (closedDeals.length === 0) return calculateScoreBasic(deal);
  const stats = buildClosedStats(closedDeals);
  return calculateScoreWithStats(deal, stats.avgClosedValue, stats.porteCounts, stats.originCounts, stats.serviceCounts, stats.catCounts, stats.closedLen);
}
